import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSetting } from '@/hooks/useSetting';
import { eventInfo } from '@/mocks/landing-data';

interface AdminLayoutProps {
  children: React.ReactNode;
  title: string;
  breadcrumbs?: { label: string; href?: string }[];
}

const sidebarLinks = [
  {
    section: 'MAIN',
    links: [
      { icon: 'ri-dashboard-3-line', label: 'Dashboard', href: '/dashboard/admin' },
    ],
  },
  {
    section: 'REGISTRATIONS',
    links: [
      { icon: 'ri-team-line', label: 'Teams', href: '/dashboard/admin/teams' },
      { icon: 'ri-user-line', label: 'Participants', href: '/dashboard/admin/participants' },
      { icon: 'ri-user-star-line', label: 'Faculty Mentors', href: '/dashboard/admin/faculty' },
    ],
  },
  {
    section: 'CONTENT',
    links: [
      { icon: 'ri-question-answer-line', label: 'Problem Statements', href: '/dashboard/admin/problem-statements' },
      { icon: 'ri-stack-line', label: 'Domains', href: '/dashboard/admin/domains' },
      { icon: 'ri-megaphone-line', label: 'Announcements', href: '/dashboard/admin/announcements' },
      { icon: 'ri-building-line', label: 'Sponsors', href: '/dashboard/admin/sponsors' },
      { icon: 'ri-price-tag-3-line', label: 'Sponsor Categories', href: '/dashboard/admin/sponsors/categories' },
    ],
  },
  {
    section: 'FINANCE',
    links: [
      { icon: 'ri-bank-card-line', label: 'Payments', href: '/dashboard/admin/payments' },
    ],
  },
  {
    section: 'INSIGHTS',
    links: [
      { icon: 'ri-file-chart-line', label: 'Reports', href: '/dashboard/admin/reports' },
      { icon: 'ri-history-line', label: 'Audit Logs', href: '/dashboard/admin/audit-logs' },
    ],
  },
  {
    section: 'SYSTEM',
    links: [
      { icon: 'ri-shield-user-line', label: 'User Management', href: '/dashboard/admin/users' },
      { icon: 'ri-settings-3-line', label: 'Settings', href: '/dashboard/admin/settings' },
    ],
  },
];

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function AdminLayout({ children, title, breadcrumbs }: AdminLayoutProps) {
  const { profile, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const eventName = useSetting('event_name', eventInfo.name);

  useEffect(() => {
    const stored = localStorage.getItem('admin-theme');
    if (stored === 'dark') {
      setIsDark(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setIsDark((prev) => {
      const next = !prev;
      localStorage.setItem('admin-theme', next ? 'dark' : 'light');
      if (next) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const handleClick = () => setShowProfileMenu(false);
    if (showProfileMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [showProfileMenu]);

  const isActive = (href: string) => {
    if (href === '/dashboard/admin') return location.pathname === '/dashboard/admin';
    if (href === '/dashboard/admin/sponsors') return location.pathname === '/dashboard/admin/sponsors' || location.pathname.startsWith('/dashboard/admin/sponsors/');
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-background-50 flex">
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full z-50 bg-background-50 border-r border-background-200/70 transition-all duration-300 flex flex-col ${
          sidebarCollapsed ? 'w-[72px]' : 'w-[260px]'
        } ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
        {/* Logo */}
        <div className={`flex items-center h-16 px-4 border-b border-background-200/70 ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}>
          <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
            <i className="ri-heart-pulse-line text-white text-lg"></i>
          </div>
          {!sidebarCollapsed && (
            <div className="min-w-0">
              <p className="font-heading text-sm font-bold text-foreground-950 truncate">{eventName}</p>
              <p className="text-[10px] text-foreground-400 leading-tight">Admin Panel</p>
            </div>
          )}
        </div>

        {/* Nav links */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
          {sidebarLinks.map((group) => (
            <div key={group.section}>
              {!sidebarCollapsed && (
                <p className="text-[10px] font-semibold text-foreground-400 tracking-wider mb-2 px-3">{group.section}</p>
              )}
              <div className="space-y-0.5">
                {group.links.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      isActive(link.href)
                        ? 'bg-primary-100 text-primary-700 font-semibold'
                        : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                    } ${sidebarCollapsed ? 'justify-center' : ''}`}
                    title={sidebarCollapsed ? link.label : undefined}
                  >
                    <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0`}>
                      <i className={`${link.icon} text-base`}></i>
                    </div>
                    {!sidebarCollapsed && <span>{link.label}</span>}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Sidebar footer */}
        <div className={`border-t border-background-200/70 p-3 ${sidebarCollapsed ? 'flex justify-center' : ''}`}>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-foreground-400 hover:bg-background-100 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap ${
              sidebarCollapsed ? 'justify-center' : 'w-full'
            }`}
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className={`${sidebarCollapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'} text-sm`}></i>
            </div>
            {!sidebarCollapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-[260px]'}`}>
        {/* Header */}
        <header className="sticky top-0 z-30 bg-background-50/80 backdrop-blur-md border-b border-background-200/70">
          <div className="flex items-center justify-between h-16 px-4 md:px-6">
            <div className="flex items-center gap-4">
              {/* Mobile menu toggle */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-menu-line text-xl text-foreground-700"></i>
              </button>

              {/* Breadcrumbs */}
              <nav className="hidden sm:flex items-center gap-2 text-sm">
                <Link to="/dashboard/admin" className="text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-home-4-line"></i>
                </Link>
                {breadcrumbs?.map((crumb, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <i className="ri-arrow-right-s-line text-foreground-300 text-xs"></i>
                    {crumb.href ? (
                      <Link to={crumb.href} className="text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap">
                        {crumb.label}
                      </Link>
                    ) : (
                      <span className="text-foreground-900 font-medium whitespace-nowrap">{crumb.label}</span>
                    )}
                  </div>
                ))}
              </nav>
            </div>

            <div className="flex items-center gap-2">
              {/* Search */}
              {showSearch && (
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search teams, participants..."
                    className="w-56 pl-9 pr-3 py-2 rounded-lg border border-background-200 bg-background-100 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors"
                    autoFocus
                  />
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                </div>
              )}
              <button
                onClick={() => setShowSearch(!showSearch)}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-search-line text-lg text-foreground-600"></i>
              </button>

              {/* Notifications */}
              <button className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer relative">
                <i className="ri-notification-3-line text-lg text-foreground-600"></i>
                <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-accent-500"></span>
              </button>

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className={`${isDark ? 'ri-sun-line' : 'ri-moon-line'} text-lg text-foreground-600`}></i>
              </button>

              {/* Profile */}
              <div className="relative">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowProfileMenu(!showProfileMenu); }}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-xs font-bold text-primary-700">
                      {profile?.full_name?.[0] || 'A'}
                    </span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-foreground-900 leading-tight whitespace-nowrap">{profile?.full_name || 'Admin'}</p>
                    <p className="text-[10px] text-foreground-400 leading-tight">{profile?.email || 'admin'}</p>
                  </div>
                </button>
                {showProfileMenu && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-background-50 border border-background-200 rounded-xl shadow-lg py-1 z-50">
                    <button
                      onClick={() => { setShowProfileMenu(false); navigate('/dashboard/admin/settings'); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <i className="ri-settings-3-line text-base"></i>
                      <span>Settings</span>
                    </button>
                    <button
                      onClick={() => { setShowProfileMenu(false); signOut(); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-logout-box-line text-base"></i>
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8">
          {/* Page header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{title}</h1>
                <p className="text-sm text-foreground-500 mt-1">
                  {getGreeting()}, {profile?.full_name || 'Admin'}
                </p>
              </div>
            </div>
          </div>

          {children}
        </main>

        {/* Footer */}
        <footer className="border-t border-background-200/70 py-4 px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-foreground-400">
            <p>&copy; 2026 {eventName} — Sona College of Technology. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </div>
  );
}